import { Link } from 'react-router-dom'
import { Note } from '../api/notes'

interface NotesListProps {
  notes: Note[]
  isLoading?: boolean
  emptyMessage?: string
  onToggleStar?: (noteId: string) => void
  onToggleHome?: (noteId: string) => void
  onDelete?: (noteId: string) => void
  selectedIds?: string[]
  onSelect?: (noteId: string) => void
  onSelectAll?: () => void
  selectionMode?: boolean
}

// Format timestamp to readable date
function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function NotesList({
  notes,
  isLoading = false,
  emptyMessage = 'No notes found',
  onToggleStar,
  onToggleHome,
  onDelete,
  selectedIds = [],
  onSelect,
  onSelectAll,
  selectionMode = false,
}: NotesListProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                </div>
                <div className="h-4 bg-gray-100 rounded w-24"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (!notes || notes.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    )
  }

  const allSelected = notes.length > 0 && selectedIds.length === notes.length
  const someSelected = selectedIds.length > 0 && selectedIds.length < notes.length

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Table Header */}
      <div className="hidden sm:grid gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ gridTemplateColumns: (selectionMode || onSelect) ? '40px minmax(200px, 1fr) minmax(150px, 200px) 140px 60px 100px' : 'minmax(200px, 1fr) minmax(150px, 200px) 140px 60px 100px' }}>
        {(selectionMode || onSelect) && (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected
              }}
              onChange={onSelectAll}
              className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
            />
          </div>
        )}
        <div>Title</div>
        <div>Tags</div>
        <div>Modified</div>
        <div className="text-center">Link</div>
        <div className="text-right">Actions</div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-100">
        {notes.map((note) => {
          const isSelected = selectedIds.includes(note.id)
          return (
          <div key={note.id} data-testid="note-row">
            {/* Desktop View */}
            <div
              className={`hidden sm:grid gap-4 p-4 hover:bg-gray-50 transition-colors group ${
                isSelected ? 'bg-primary-50' : ''
              }`}
              style={{ gridTemplateColumns: (selectionMode || onSelect) ? '40px minmax(200px, 1fr) minmax(150px, 200px) 140px 60px 100px' : 'minmax(200px, 1fr) minmax(150px, 200px) 140px 60px 100px' }}
            >
            {/* Checkbox */}
            {(selectionMode || onSelect) && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onSelect?.(note.id)}
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
                />
              </div>
            )}

            {/* Title */}
            <div className="flex items-center min-w-0">
              <Link
                to={`/notes/${note.id}`}
                className="font-medium text-gray-900 hover:text-primary truncate transition-colors"
              >
                {note.name || 'Untitled'}
              </Link>
              {note.is_starred && (
                <svg className="w-4 h-4 text-yellow-500 ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
            </div>

            {/* Tags */}
            <div className="flex items-start flex-wrap gap-1 py-1">
              {note.tags?.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                >
                  {tag.name}
                </span>
              ))}
              {note.tags && note.tags.length > 3 && (
                <span className="text-xs text-gray-400">+{note.tags.length - 3}</span>
              )}
            </div>

            {/* Date */}
            <div className="flex items-center text-sm text-gray-500">
              {formatDate(note.modified_time || note.created_time)}
            </div>

            {/* External Link */}
            <div className="flex items-center justify-center">
              {note.source_url && (
                <a
                  href={note.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary transition-colors p-1"
                  title="Open source"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onToggleStar && (
                <button
                  onClick={() => onToggleStar(note.id)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    note.is_starred 
                      ? 'text-yellow-500 hover:text-yellow-600' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title={note.is_starred ? 'Unstar' : 'Star'}
                >
                  <svg className="w-4 h-4" fill={note.is_starred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </button>
              )}
              {onToggleHome && (
                <button
                  onClick={() => onToggleHome(note.id)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    note.is_home 
                      ? 'text-primary hover:text-secondary' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title={note.is_home ? 'Remove from home' : 'Add to home'}
                >
                  <svg className="w-4 h-4" fill={note.is_home ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(note.id)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              <Link
                to={`/notes/${note.id}`}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary transition-colors"
                title="Open"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            </div>

            {/* Mobile View */}
            <div className={`sm:hidden p-4 border-b border-gray-100 ${isSelected ? 'bg-primary-50' : ''}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center min-w-0 flex-1">
                  {(selectionMode || onSelect) && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelect?.(note.id)}
                      className="w-4 h-4 mr-3 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer flex-shrink-0"
                    />
                  )}
                  <Link
                    to={`/notes/${note.id}`}
                    className="font-medium text-gray-900 hover:text-primary transition-colors truncate"
                  >
                    {note.name || 'Untitled'}
                  </Link>
                </div>
                {note.is_starred && (
                  <svg className="w-4 h-4 text-yellow-500 ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                )}
              </div>
              {note.tags && note.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {note.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                    >
                      {tag.name}
                    </span>
                  ))}
                  {note.tags.length > 3 && (
                    <span className="text-xs text-gray-400">+{note.tags.length - 3}</span>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{formatDate(note.modified_time || note.created_time)}</span>
                <div className="flex items-center space-x-2">
                  {note.source_url && (
                    <a
                      href={note.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-primary transition-colors"
                      title="Open source"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
          )
        })}
      </div>
    </div>
  )
}
